import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import TextField from "@mui/material/TextField";
import { Button, IconButton, Input, InputAdornment } from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import {
  AgentList,
  CommonBody,
  CompanyList,
  UsersList,
} from "../../types/General";
import {
  useAddSalariedAgentMutation,
  useUpdateUserMutation,
  useUserDetailsMutation,
} from "../../service/users";
import { errorToast, showWarning, successToast } from "../../helpers";
import { UploadMedia } from "../../utils/mediaUpload";
import PhoneInput from "react-phone-input-2";
import { isNumber } from "../../utils/validations";
import Loader from "../../constants/Loader";
import { USER_TYPES } from "../../utils/enums";
import { useFaceCheckImageMutation } from "../../service/auth";
import { toast } from "react-toastify";
import { Autocomplete } from "@react-google-maps/api";

const AddSalariedAgents = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState<string>("");
  const [faceCheckApi] = useFaceCheckImageMutation();
  const [profile_picture_id, setProfile_picture_id] = useState<number | null>(
    null
  );
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<
    UsersList | AgentList | CompanyList | null
  >(null);

  const [getUserDetails] = useUserDetailsMutation();
  const [addSalariedAgent] = useAddSalariedAgentMutation();
  const [updateSalariedAgent] = useUpdateUserMutation();

  const getDetails = async (id: number) => {
    setLoading(true);
    const body = {
      user_type: USER_TYPES.AGENT,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getUserDetails({
        body: encryptedBody,
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        setDetails(response?.data);
        setProfileImage(response?.data?.profile_picture?.media_url || "");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.[0]?.media_url) {
      faceCheckImage(imageUrl?.data?.[0]?.media_url, imageUrl?.data?.[0]?.id);
    }
  };

  const faceCheckImage = async (url: string, id: number) => {
    const body = {
      image_link: url,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await faceCheckApi(encryptedBody).unwrap();
      if (response?.status === 200) {
        if (response?.data?.contains_face) {
          setProfileImage(url || "");
          setProfile_picture_id(id || null);
        } else {
          toast.warning("Please add a valid image which includes a face");
        }
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue("dialCode", country?.dialCode);
    formik.setFieldValue("countryCode", country?.countryCode);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: details?.email || "",
      firstName: details?.first_name || "",
      middleName: details?.middle_name || "",
      lastName: details?.last_name || "",
      dialCode: details?.country_code || "+971",
      countryCode: details?.country_idd || "ae",
      phone: details?.phone_no || "",
      address: details?.address || "",
      city: details?.city || "",
      state: details?.state || "",
      pincode: details?.pin_code || "",
      lat: details?.latitude || "",
      lng: details?.longitude || "",
      country: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required!")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address!"
        ),
      phone: Yup.string()
        .required("Phone number is required")
        .min(6, "Minimum 6 digits are required")
        .max(16, "Maximum 16 digits are allowed"),
      firstName: Yup.string()
        .required("First name is required")
        .min(2, "Minimum 2 characters are required"),

      address: Yup.string().required("Address is required"),
      city: Yup.string().required("City is required"),
      pincode: Yup.string()
        .required("Pin code is required")
        .label("Pin Code")
        .min(4, "Minimum 4 digits are required"),
      state: Yup.string().required("State is required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        ...(id ? { user_type: USER_TYPES.AGENT } : {}),
        profile_picture: profile_picture_id,
        email: values.email,
        phone_no: values.phone,
        country_code:
          (values.dialCode.includes("+") ? "" : "+") + values.dialCode,
        country_idd: values.countryCode?.toUpperCase(),
        first_name: values.firstName,
        middle_name: values.middleName,
        last_name: values.lastName,
        fullName: [values.firstName, values.middleName, values.lastName]
          .filter(Boolean)
          .join(" "),
        address: values.address,
        city: values.city,
        state: values.state,
        pin_code: values.pincode,
        latitude: Number(values.lat),
        longitude: Number(values.lng),
        step: 7,
      };
      if (!values?.lat || !values?.lng) {
        showWarning("Please select a valid address.");
        return;
      }

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateSalariedAgent({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addSalariedAgent(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Wizard team member updated successfully");
          } else {
            successToast("Wizard team member added successfully");
          }
          navigate("/users/agent");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  const onLoad = (autocompleteObj: any) => {
    setAutocomplete(autocompleteObj);
  };
  const onPlaceChanged = async () => {
    if (autocomplete) {
      const place = await (autocomplete as any).getPlace();

      if (place && place.address_components) {
        const address = place.address_components;

        let state,
          city,
          country,
          zip = "";

        address.forEach(function (component: any) {
          const types = component.types;

          if (
            types.indexOf("locality") > -1 ||
            types.indexOf("administrative_area_level_3") > -1
          ) {
            city = component.long_name;
          }
          if (types.indexOf("postal_code") > -1) {
            zip = component.long_name;
          }
          if (types.indexOf("administrative_area_level_1") > -1) {
            state = component?.long_name || "";
          }
          if (types.indexOf("country") > -1) {
            country = component?.long_name || "";
          }
        });

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        // setAddress(true);
        formik.setFieldValue("address", `${place?.formatted_address}`);
        formik.setFieldValue("city", city || "");
        formik.setFieldValue("state", state || "");
        formik.setFieldValue("pincode", zip || "");
        formik.setFieldValue("lat", lat || "");
        formik.setFieldValue("lng", lng || "");
        formik.setFieldValue("country", country || "");
      }
    }
  };

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Wizard Team Member</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
            Users - {id ? "Edit" : "Add"} Wizard Team Member
          </p>
        </div>{" "}
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Profile Image</label>
              <div className="upload_box">
                <label>
                  {profileImage && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => setProfileImage("")}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={
                        profileImage
                          ? profileImage
                          : "/static/images/user_placeholder.png"
                      }
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
            </div>
            <div className="control_group w_50" />
            <div className="control_group w_50">
              <label>First Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="firstName"
                id="firstName"
                inputProps={{ maxLength: 30 }}
                value={formik.values.firstName}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </div>
            <div className="control_group w_50">
              <label>Middle Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="middleName"
                id="middleName"
                inputProps={{ maxLength: 30 }}
                value={formik.values.middleName}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.middleName && formik.errors.middleName
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Last Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="lastName"
                id="lastName"
                inputProps={{ maxLength: 30 }}
                value={formik.values.lastName}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </div>

            <div className="control_group w_50">
              <label>E-mail</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                className="text_field"
                hiddenLabel
                name="email"
                id="email"
                fullWidth
                inputProps={{ maxLength: 80 }}
                value={formik.values.email}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>

            <div className="control_group w_50">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={formik.values.dialCode + formik.values.phone}
                country={"us"}
                inputClass="phoneInput"
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
                onChange={(phone, country) => handleChangePhone(phone, country)}
              />
              {formik.touched.phone && formik.errors.phone ? (
                <h6 className="err_msg">
                  {formik.touched.phone && formik.errors.phone}
                </h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_50">
              <label>Complete Address</label>
              <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                <TextField
                  variant="outlined"
                  placeholder="Enter here"
                  fullWidth
                  hiddenLabel
                  name="address"
                  id="address"
                  className="text_field"
                  inputProps={{ maxLength: 100 }}
                  value={formik.values.address}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                      return;
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.address && formik.errors.address}
                  InputProps={{
                    endAdornment: formik.values.address ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => formik.setFieldValue("address", "")}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Autocomplete>
            </div>
            <div className="control_group w_50">
              <label> State</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="state"
                id="state"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.state}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.state && formik.errors.state}
              />
            </div>
            <div className="control_group w_50">
              <label> City</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="city"
                id="city"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.city}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.city && formik.errors.city}
              />
            </div>
            <div className="control_group w_50">
              <label> Pin Code</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="pincode"
                id="pincode"
                className="text_field"
                inputProps={{ maxLength: 10 }}
                value={formik.values.pincode}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                    return;
                  } else if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.pincode && formik.errors.pincode}
              />
            </div>
          </div>
          <div className="form_btn end">
            <Button className="btn btn_secondary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddSalariedAgents;
